<script lang="ts">
    import dayjs from 'dayjs'
    import 'dayjs/locale/pt-br'
    dayjs.locale('pt-br')
    import type { Schedule } from '../../../modules/schedulerApi/schedules/schedule'

    export let schedule: Schedule
    export let groups: { scheduleGroupId: string; name: string }[] = []
    export let today: boolean = false
    // colunas que não são agenda (ex.: "Minhas notas") trocam o horário de funcionamento por um resumo próprio
    export let subtitleOverride: string = null

    const weekdays = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado']

    // a mesma coluna representa dois eixos: uma AGENDA (diário, tem label) ou um DIA (semanal, label vazia).
    // É assim que o componente original distinguia os dois casos — mantido para não mudar comportamento.
    $: isScheduleColumn = !!schedule.label
    $: title = isScheduleColumn ? schedule.label : weekdays[schedule.selectedDate?.getDay() || 0]
    $: icon = isScheduleColumn ? schedule.label.substring(0, 1) : dayjs(schedule.selectedDate).format('DD')

    // grupo de agendas é opcional: quando a agenda não tem grupo, a linha exibe só o horário de funcionamento.
    // Na visão semanal as 7 colunas são a MESMA agenda, então repetir o grupo 7x não agrega — só o horário.
    $: groupName = isScheduleColumn ? groups.find((el) => el.scheduleGroupId === schedule.scheduleGroupId)?.name || '' : ''
    $: subtitle = subtitleOverride !== null ? subtitleOverride : [groupName, openingHours(schedule)].filter(Boolean).join(' · ')

    function minuteLabel(minute: number) {
        const h = Math.floor(minute / 60)
        const m = minute % 60
        return m === 0 ? String(h).padStart(2, '0') : String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0')
    }

    function openingHours(s: Schedule) {
        if (s.startMinute === undefined || s.startMinute === null) return ''
        if (s.endMinute === undefined || s.endMinute === null) return ''
        return minuteLabel(s.startMinute) + '–' + minuteLabel(s.endMinute) + 'h'
    }
</script>

<div class="list-header" class:is-weekly-today={today} id="isToday_{today}">
    <div class="list-label nowrap">
        <div class="list-label_icon" class:list-label_icon-today={today}>
            {icon}
        </div>
        <div class="list-label_text">
            <div class="list-label_label">{title}</div>
            {#if subtitle}
                <div class="list-label_sub">{subtitle}</div>
            {/if}
        </div>
        {#if today}
            <div class="badge badge-today list-label_today">hoje</div>
        {/if}
    </div>
</div>

<style>
    /* valores base = tema escuro (padrão do app, inclusive quando o body não tem classe de tema).
       O tema claro sobrescreve em css_theme_light.css. */
    .list-header {
        padding: 0px 10px;
        display: flex;
        align-items: center;
        background: #333;
        border-bottom: 1px solid #2a2a2a;
        position: sticky;
        /* no mobile gruda abaixo da parte visível dos controls da agenda (--sticky-top, definido em
           schedules-container.svelte); no desktop a variável não existe e fica 0 */
        top: var(--sticky-top, 0px);
        /* precisa ficar ENTRE duas coisas: acima do bloco de agendamento expandido no hover (z-index 3),
           para ele passar por baixo ao rolar, e abaixo do menu do Zdrop (z-index 5, ver zdrop.svelte),
           senão o menu de 3 pontos do mobile abre escondido atrás desta faixa. */
        z-index: 4;
        width: 100%;
        min-height: 52px;
    }

    .list-header > div:first-child {
        flex-grow: 1;
        min-width: 0;
    }

    .list-label {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        min-width: 0;
    }

    .list-label_icon {
        width: 32px;
        height: 32px;
        flex: 0 0 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #66666660;
        border-radius: 50%;
        margin: 8px;
        margin-left: 0px;
        font-size: 13px;
        font-weight: 600;
    }

    .list-label_text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-width: 0;
        line-height: 1.25;
    }

    .list-label_label {
        font-weight: 600;
        font-size: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .list-label_sub {
        font-size: 11.5px;
        color: #9a9a9a;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .list-label_today {
        margin-left: 8px;
        flex-shrink: 0;
    }

    .is-weekly-today {
        color: #328866;
    }

    .badge-today {
        margin-right: 5px;
        background: #328866;
        font-size: 10px;
    }

    @media print {
        .list-header {
            padding: 0px 0px;
            position: unset;
            justify-content: center;
            width: 100%;
            display: unset !important;
            font-size: 20px;
            min-height: unset;
        }
    }
</style>
